import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { H3 } from '../../../AbstractElements';
import Loader from '../../../Components/Widgets/Loader/generic';
import { get, postJson } from '../../../hook/request';

type FilePresenze = {
  ID: number;
  month: string;
  anno: number;
  data_creazione: string;
  path_file: string;
};

function getCurrentMonth(): string {
  const date = new Date();

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');

  return `${year}-${month}`;
}

function extractFiles(value: any): FilePresenze[] {
  const root = value?.data ?? value;

  const list = Array.isArray(root)
    ? root
    : Array.isArray(root?.data)
      ? root.data
      : Array.isArray(root?.files)
        ? root.files
        : [];

  return list.map((item: any) => ({
    ID: Number(item.ID ?? item.id ?? 0),
    month: String(item.month ?? ''),
    anno: Number(item.anno ?? 0),
    data_creazione: String(
      item.data_creazione ??
      item.created_at ??
      ''
    ),
    path_file: String(item.path_file ?? ''),
  }));
}

function formatDateTime(value?: string): string {
  if (!value) {
    return '-';
  }

  const normalizedValue = value.replace(' ', 'T');
  const date = new Date(normalizedValue);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}


function getFileName(path: string): string {
  if (!path) {
    return 'Scarica file';
  }

  const cleanPath = path.split('?')[0];
  const fileName = cleanPath.split('/').pop();

  return fileName || 'Scarica file';
}

function getDownloadUrl(path: string): string {
  if (!path) {
    return '#';
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  if (path.startsWith('/')) {
    return path;
  }

  return `/${path}`;
}

export default function GestioneFilePresenze() {
  const [rows, setRows] = useState<FilePresenze[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState<string>(
    getCurrentMonth()
  );

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);

      const response = await get<
        | FilePresenze[]
        | {
            data?: FilePresenze[];
            files?: FilePresenze[];
          }
      >('/presenze/getFiles');

      setRows(extractFiles(response));
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          'Errore nel caricamento dei file'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleCreateFile = async () => {
    if (!selectedMonth) {
      toast.warn('Seleziona il mese da elaborare');
      return;
    }

    const [annoValue, meseValue] = selectedMonth.split('-');

    const anno = Number(annoValue);
    const mese = Number(meseValue);

    if (!anno || !mese) {
      toast.warn('La data selezionata non è valida');
      return;
    }

    try {
      setCreating(true);

      const response: any = await postJson('/presenze/createFile', {
        data: selectedMonth,
        mese,
        anno,
      });

      toast.success(
        response?.message ??
          response?.data?.message ??
          'File creato con successo'
      );

      await loadFiles();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          'Errore durante la creazione del file'
      );
    } finally {
      setCreating(false);
    }
  };

  const columns: TableColumn<FilePresenze>[] = useMemo(
    () => [
      {
        id: 'month',
        name: 'Mese',
        selector: row => row.month,
        sortable: true,
        width: '150px',
        cell: row => row.month,
      },
      {
        id: 'anno',
        name: 'Anno',
        selector: row => row.anno,
        sortable: true,
        width: '110px',
      },
      {
        id: 'data_creazione',
        name: 'Data creazione',
        selector: row => row.data_creazione,
        sortable: true,
        width: '220px',
        cell: row => formatDateTime(row.data_creazione),
      },
      {
        id: 'path_file',
        name: 'File',
        selector: row => row.path_file,
        sortable: true,
        grow: 2,
        cell: row => {
          if (!row.path_file) {
            return (
              <span className="text-muted">
                File non disponibile
              </span>
            );
          }

          return (
            <a
              href={getDownloadUrl(row.path_file)}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="btn btn-sm btn-outline-primary"
              onClick={event => event.stopPropagation()}
            >
              {getFileName(row.path_file)}
            </a>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="page-body pt-4">
      <Card>
        <CardBody>
          <H3 className="mb-4">File presenze</H3>

          <Row className="g-3 align-items-end mb-4">
            <Col xs={12} md={4} lg={3}>
              <Label for="mese-presenze">
                Mese da elaborare
              </Label>

              <Input
                id="mese-presenze"
                type="month"
                value={selectedMonth}
                onChange={event =>
                  setSelectedMonth(event.target.value)
                }
                disabled={creating}
              />
            </Col>

            <Col xs={12} md="auto">
              <Button
                color="primary"
                type="button"
                onClick={handleCreateFile}
                disabled={creating || !selectedMonth}
              >
                {creating ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creazione...
                  </>
                ) : (
                  'Crea file'
                )}
              </Button>
            </Col>
          </Row>

          <div className="table-responsive">
            <DataTable
              columns={columns}
              data={rows}
              progressPending={loading}
              progressComponent={<Loader />}
              striped
              pagination
              highlightOnHover
              dense
              noDataComponent="Nessun file disponibile"
              paginationPerPage={25}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              defaultSortFieldId="data_creazione"
              defaultSortAsc={false}
              customStyles={{
                rows: {
                  style: {
                    minHeight: '52px',
                  },
                },
              }}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
}